import { ERROR_CODES, ValidationError } from '@ibe/errors';
/**
 * Monetary arithmetic.
 *
 * The master prompt and Task 19's money invariants both require decimal
 * arithmetic and forbid floating point. Amounts are therefore held as `bigint`
 * counts of the currency's minor unit — cents for AUD, yen for JPY, fils for
 * KWD — so addition and subtraction are exact by construction and there is no
 * representable `0.1 + 0.2` problem.
 *
 * A Money value always carries its currency. Operations across mismatched
 * currencies throw rather than silently coercing: a booking totalled in the
 * wrong currency is a financial defect, not a rounding nuisance.
 */
/** ISO 4217 minor-unit exponents that are not 2. */
const CURRENCY_EXPONENT_OVERRIDES = {
    BIF: 0,
    CLP: 0,
    DJF: 0,
    GNF: 0,
    ISK: 0,
    JPY: 0,
    KMF: 0,
    KRW: 0,
    PYG: 0,
    RWF: 0,
    UGX: 0,
    UYI: 0,
    VND: 0,
    VUV: 0,
    XAF: 0,
    XOF: 0,
    XPF: 0,
    BHD: 3,
    IQD: 3,
    JOD: 3,
    KWD: 3,
    LYD: 3,
    OMR: 3,
    TND: 3,
};
const DEFAULT_EXPONENT = 2;
const DECIMAL_RADIX = 10;
/** Minor units per major unit for a currency, e.g. 100 for AUD, 1 for JPY. */
export function currencyExponent(currency) {
    return CURRENCY_EXPONENT_OVERRIDES[currency.toUpperCase()] ?? DEFAULT_EXPONENT;
}
/** How to resolve a fractional minor unit. */
export const ROUNDING_MODE = {
    /** Toward the nearer neighbour; ties away from zero. Standard for retail. */
    HALF_UP: 'HALF_UP',
    /** Toward the nearer neighbour; ties toward the even neighbour. */
    HALF_EVEN: 'HALF_EVEN',
    /** Toward zero. */
    DOWN: 'DOWN',
    /** Away from zero. */
    UP: 'UP',
};
const CURRENCY_PATTERN = /^[A-Z]{3}$/u;
const AMOUNT_PATTERN = /^(-)?(\d+)(?:\.(\d+))?$/u;
function assertCurrency(currency) {
    const upper = currency.toUpperCase();
    if (!CURRENCY_PATTERN.test(upper)) {
        throw new ValidationError([
            {
                field: 'currency',
                message: `Invalid ISO 4217 code: ${currency}`,
                code: 'invalid_currency',
            },
        ], { message: 'Invalid currency' });
    }
    return upper;
}
/** Builds a Money directly from minor units. */
export function money(amountMinor, currency) {
    return { amountMinor, currency: assertCurrency(currency) };
}
/** Zero in the given currency. */
export function zero(currency) {
    return money(0n, currency);
}
/**
 * Parses a decimal string such as `"149.95"` into exact minor units.
 *
 * A string, never a number: passing `149.95` as a JS number has already lost
 * precision before this function can see it. More decimal places than the
 * currency supports is an error, not something to silently round away.
 */
export function moneyFromDecimalString(value, currency) {
    const code = assertCurrency(currency);
    const match = AMOUNT_PATTERN.exec(value.trim());
    if (match === null) {
        throw new ValidationError([{ field: 'amount', message: `Not a decimal amount: ${value}`, code: 'invalid_amount' }], { message: 'Invalid monetary amount' });
    }
    const [, sign, whole = '0', fraction = ''] = match;
    const exponent = currencyExponent(code);
    if (fraction.length > exponent) {
        throw new ValidationError([
            {
                field: 'amount',
                message: `${code} supports ${String(exponent)} decimal place(s); received ${String(fraction.length)}`,
                code: 'excess_precision',
            },
        ], { message: 'Monetary amount has more precision than the currency allows' });
    }
    const minor = BigInt(whole + fraction.padEnd(exponent, '0'));
    return { amountMinor: sign === '-' ? -minor : minor, currency: code };
}
/**
 * Parses a database `numeric(p,4)` value into Money.
 *
 * Drizzle's `moneyColumn` stores the currency's natural precision padded with
 * trailing zeros to a 4-decimal scale, which `moneyFromDecimalString` rejects.
 * This helper strips the padding first.
 */
export function moneyFromNumeric(value, currency) {
    if (value === null || value === '')
        return money(0n, currency);
    const trimmed = value.replace(/0+$/u, '').replace(/\.$/u, '');
    if (trimmed === '' || trimmed === '-')
        return money(0n, currency);
    return moneyFromDecimalString(trimmed, currency);
}
/** Renders as a plain decimal string, e.g. `"149.95"`. No symbol, no grouping. */
export function moneyToDecimalString(value) {
    const exponent = currencyExponent(value.currency);
    const negative = value.amountMinor < 0n;
    const absolute = negative ? -value.amountMinor : value.amountMinor;
    if (exponent === 0)
        return `${negative ? '-' : ''}${absolute.toString()}`;
    const divisor = BigInt(DECIMAL_RADIX) ** BigInt(exponent);
    const whole = absolute / divisor;
    const fraction = (absolute % divisor).toString().padStart(exponent, '0');
    return `${negative ? '-' : ''}${whole.toString()}.${fraction}`;
}
function assertSameCurrency(left, right) {
    if (left.currency !== right.currency) {
        throw new ValidationError([
            {
                field: 'currency',
                message: `Cannot combine ${left.currency} with ${right.currency}`,
                code: 'currency_mismatch',
            },
        ], { message: 'Currency mismatch' });
    }
}
export function addMoney(left, right) {
    assertSameCurrency(left, right);
    return { amountMinor: left.amountMinor + right.amountMinor, currency: left.currency };
}
export function subtractMoney(left, right) {
    assertSameCurrency(left, right);
    return { amountMinor: left.amountMinor - right.amountMinor, currency: left.currency };
}
/** Sums a list. An empty list needs an explicit currency to stay well-typed. */
export function sumMoney(values, currency) {
    return values.reduce((total, value) => addMoney(total, value), zero(currency));
}
/** Negative for left < right, zero for equal, positive for left > right. */
export function compareMoney(left, right) {
    assertSameCurrency(left, right);
    if (left.amountMinor < right.amountMinor)
        return -1;
    return left.amountMinor > right.amountMinor ? 1 : 0;
}
export function moneyEquals(left, right) {
    return left.currency === right.currency && left.amountMinor === right.amountMinor;
}
export function isNegative(value) {
    return value.amountMinor < 0n;
}
export function isZero(value) {
    return value.amountMinor === 0n;
}
/**
 * Divides `numerator / denominator` in minor units under an explicit rounding
 * mode. The primitive behind percentage discounts, tax rates and currency
 * conversion — each of which must state how it rounds rather than inheriting
 * whatever the language does.
 */
function divideRounded(numerator, denominator, mode) {
    if (denominator === 0n) {
        throw new ValidationError([{ field: 'denominator', message: 'Division by zero', code: 'division_by_zero' }], { message: 'Division by zero' });
    }
    const negative = numerator < 0n !== denominator < 0n;
    const absNumerator = numerator < 0n ? -numerator : numerator;
    const absDenominator = denominator < 0n ? -denominator : denominator;
    const quotient = absNumerator / absDenominator;
    const remainder = absNumerator % absDenominator;
    if (remainder === 0n)
        return negative ? -quotient : quotient;
    const twiceRemainder = remainder * 2n;
    let rounded = quotient;
    switch (mode) {
        case ROUNDING_MODE.DOWN:
            break;
        case ROUNDING_MODE.UP:
            rounded = quotient + 1n;
            break;
        case ROUNDING_MODE.HALF_UP:
            if (twiceRemainder >= absDenominator)
                rounded = quotient + 1n;
            break;
        case ROUNDING_MODE.HALF_EVEN:
            if (twiceRemainder > absDenominator)
                rounded = quotient + 1n;
            else if (twiceRemainder === absDenominator && quotient % 2n === 1n)
                rounded = quotient + 1n;
            break;
    }
    return negative ? -rounded : rounded;
}
/**
 * Multiplies by an exact rational `numerator / denominator`.
 *
 * Rates are passed as rationals rather than floats so a 7.5% tax is
 * `multiplyMoney(total, 75n, 1000n)` — exact — rather than `* 0.075`, which
 * is not representable in binary floating point.
 */
export function multiplyMoney(value, numerator, denominator = 1n, mode = ROUNDING_MODE.HALF_UP) {
    return {
        amountMinor: divideRounded(value.amountMinor * numerator, denominator, mode),
        currency: value.currency,
    };
}
const PERCENT_DENOMINATOR = 100n;
const BASIS_POINT_DENOMINATOR = 10000n;
/** Applies a percentage, e.g. `percentageOf(total, 15n)` for 15%. */
export function percentageOf(value, percent, mode = ROUNDING_MODE.HALF_UP) {
    return multiplyMoney(value, percent, PERCENT_DENOMINATOR, mode);
}
/** Applies basis points, e.g. `750n` for 7.5%. Use where sub-1% precision matters. */
export function basisPointsOf(value, basisPoints, mode = ROUNDING_MODE.HALF_UP) {
    return multiplyMoney(value, basisPoints, BASIS_POINT_DENOMINATOR, mode);
}
/**
 * Splits an amount into `parts` shares that sum back to exactly the original.
 *
 * Naive division loses or invents minor units — splitting 10.00 three ways
 * gives 3.33 × 3 = 9.99. The remainder is distributed one minor unit at a time
 * across the leading shares so the total always reconciles, which is what
 * per-night rate breakdowns and multi-room splits require.
 */
export function allocateMoney(value, parts) {
    if (!Number.isInteger(parts) || parts <= 0) {
        throw new ValidationError([{ field: 'parts', message: 'Must be a positive integer', code: 'invalid_parts' }], { message: 'Invalid allocation' });
    }
    const divisor = BigInt(parts);
    const base = divideRounded(value.amountMinor, divisor, ROUNDING_MODE.DOWN);
    const distributed = base * divisor;
    let remainder = value.amountMinor - distributed;
    const step = remainder < 0n ? -1n : 1n;
    const shares = [];
    for (let index = 0; index < parts; index += 1) {
        const extra = remainder === 0n ? 0n : step;
        if (remainder !== 0n)
            remainder -= step;
        shares.push({ amountMinor: base + extra, currency: value.currency });
    }
    return shares;
}
/**
 * Converts between currencies using an exact rational rate.
 *
 * Deliberately requires the rate as a rational and the target currency
 * explicitly. Task 11 must persist the rate snapshot used for a quote; a
 * conversion helper that hides the rate makes that impossible to audit.
 */
export function convertMoney(value, targetCurrency, rateNumerator, rateDenominator, mode = ROUNDING_MODE.HALF_UP) {
    const code = assertCurrency(targetCurrency);
    const sourceExponent = currencyExponent(value.currency);
    const targetExponent = currencyExponent(code);
    // Rebase minor units when the currencies have different exponents, e.g.
    // AUD (2) to JPY (0).
    const exponentDelta = targetExponent - sourceExponent;
    const scaleNumerator = exponentDelta > 0 ? BigInt(DECIMAL_RADIX) ** BigInt(exponentDelta) : 1n;
    const scaleDenominator = exponentDelta < 0 ? BigInt(DECIMAL_RADIX) ** BigInt(-exponentDelta) : 1n;
    return {
        amountMinor: divideRounded(value.amountMinor * rateNumerator * scaleNumerator, rateDenominator * scaleDenominator, mode),
        currency: code,
    };
}
/** Guards an amount that must not be negative, e.g. a capture or refund. */
export function assertNonNegative(value, field = 'amount') {
    if (isNegative(value)) {
        throw new ValidationError([{ field, message: 'Amount must not be negative', code: 'negative_amount' }], { message: 'Negative monetary amount', metadata: { code: ERROR_CODES.VALIDATION_ERROR } });
    }
    return value;
}
//# sourceMappingURL=money.js.map